import {
  Box,
  HStack,
  IconButton,
  Flex,
  Portal,
  Image as ChakraImage,
  Icon,
} from '@chakra-ui/react'
import { MouseEventHandler, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'react-feather'
import { FocusLock } from '@chakra-ui/focus-lock'

import { Caption, Label } from 'atoms/typography'
import ImageCarouselControls from 'components/ImageCarouselControls'
import Overlay from 'components/Image/Overlay'
import useCarouselState from 'lib/hooks/useCarouselState'
import { ImageData } from 'components/ImageCarousel'
import useScrollLock from 'lib/hooks/useScrollLock'

import useEscapeToClose from './useEscapeToClose'

export interface LightboxProps {
  images: ImageData[]
  initialSelected?: number
  onClose: () => void
}

const scrollbarStyle = {
  '&::-webkit-scrollbar': {
    width: 1,
  },
  '&::-webkit-scrollbar-track': {
    width: 2,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'dusk',
    borderRadius: '24px',
  },
}

const Lightbox: React.FC<LightboxProps> = ({
  images,
  onClose,
  initialSelected = 0,
}) => {
  const { t } = useTranslation()

  const carouselState = useCarouselState({
    initial: initialSelected,
    count: images.length,
  })

  useScrollLock()

  const closeButtonRef = useRef<HTMLButtonElement | null>(null)

  const onCloseCallback: MouseEventHandler = event => {
    if (event.currentTarget === event.target) {
      onClose()
    }
  }

  useEscapeToClose({ onClose })

  const image = images[carouselState.selected]

  return (
    <Portal>
      <FocusLock initialFocusRef={closeButtonRef}>
        <LightboxContent onClick={onCloseCallback}>
          <HStack
            w="100%"
            justifyContent="space-between"
            onClick={onCloseCallback}
          >
            <Box ml="3">
              <Label size="md" color="dusk">
                {t('lightbox.hitEsc')}
              </Label>
            </Box>
            <IconButton
              ref={closeButtonRef}
              icon={<Icon boxSize={6} as={X} />}
              variant="ghost"
              color="dusk"
              aria-label={t('lightbox.closeButtonAriaLabel')}
              onClick={onClose}
            />
          </HStack>

          <Flex
            w="90%"
            h="95%"
            mx="center"
            alignItems="center"
            justifyContent="space-around"
            onClick={onCloseCallback}
          >
            <Flex
              flexDirection="column"
              alignItems="center"
              justifyContent="space-around"
              display="table"
              onClick={e => e.stopPropagation()}
            >
              <Box position="relative">
                {images.length > 1 ? (
                  <Overlay>
                    <ImageCarouselControls
                      carouselState={carouselState}
                      ariaControlsId="lightboxContainer"
                    />
                  </Overlay>
                ) : null}

                <ChakraImage
                  minH={{ base: 'auto', sm: '20vh' }}
                  maxH={{ base: '60vh', sm: '70vh' }}
                  src={image.url}
                  fallback={<Box minW="2xl" minH="xl" bg="charcoal" />}
                />
              </Box>

              {image.caption ? (
                <Box
                  p="2"
                  bgColor="charcoal"
                  textAlign="left"
                  display="table-caption"
                  overflowY="scroll"
                  maxH={{ base: '16', sm: '24' }}
                  sx={{ captionSide: 'bottom', ...scrollbarStyle }}
                >
                  <Caption color="offWhite">{image.caption}</Caption>
                </Box>
              ) : null}
            </Flex>
          </Flex>
        </LightboxContent>
      </FocusLock>
    </Portal>
  )
}

interface LightboxContentProps {
  onClick: MouseEventHandler<Element>
}

const LightboxContent: React.FC<LightboxContentProps> = ({
  onClick,
  children,
}) => (
  <Box
    h="100vh"
    w="100vw"
    left="0"
    top="0"
    pb="3"
    pt={{ base: '3', md: '8' }}
    px={{ base: '3', md: '8' }}
    zIndex="fullScreenOverlay"
    position="fixed"
    bgColor="lightBoxOverlay"
    onClick={onClick}
    id="lightboxContainer"
  >
    {children}
  </Box>
)

export default Lightbox
